import { namePatternPvq, suffixPattern } from 'constants/patterns';
import { nameSuffixes } from 'constants/enums/nameSuffixOptions';
import { ValidateJSModel, PVQNameModel, BranchCollectionModel } from 'forms/shared/types';
import { ForeignContactsItemModel, ForeignContactsItemAliasesModel } from 'forms/sections/foreign/contacts/types';
import { isPVQForm } from 'helpers/form';
import { DeepSafePartial } from 'types/types';

export const hasNameDetails = (name:DeepSafePartial<PVQNameModel>|undefined) => {
  if (name?.isAliasOrNickname
    || name?.first
    || name?.middle
    || name?.last
  ) {
    return true;
  }
  return false;
};
const pvqNameModelValidator: ValidateJSModel<PVQNameModel> = {
  isAliasOrNickname: {},

  first: (value, attributes, attributeName, options) => {
    if (attributes?.noFirstName || attributes?.firstUnknown) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.first.dataIntegrity.checkedWithValue',
        },
      };
    }
    if (attributes?.firstLettersOnly) {
      return {
        presence: true,
        length: {
          minimum: 1,
          maximum: 100,
          wrongLength: 'error.pvq.name.lettersonly.length'
        },
        format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
      };
    }
    return {
      presence: true,
      format: { pattern: namePatternPvq, message: 'error.pvq.name.first.pattern' },
      length: (value, attributes) => ({ // eslint-disable-line no-shadow
        minimum: 2,
        maximum: 100,
        tooShort: 'error.pvq.name.first.length.tooshort',
        tooLong: 'error.pvq.name.first.length.toolong',
      }),
    };
  },

  firstLettersOnly: {},

  noFirstName: {},

  hideNoFirstName: {},

  last: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.isAliasOrNickname) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.last.dataIntegrity.aliasOrNickname',
        }
      };
    }

    if (attributes?.lastUnknown) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.last.dataIntegrity.checkedWithValue',
        },
      };
    }

    if (attributes?.lastLettersOnly) {
      return {
        presence: true,
        length: {
          minimum: 1,
          maximum: 100,
          wrongLength: 'error.pvq.name.lettersonly.length'
        },
        format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
      };
    }
    return {
      format: { pattern: namePatternPvq, message: 'error.pvq.name.last.pattern' },
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooLong: 'error.pvq.name.last.length'
      },
    };
  },

  lastLettersOnly: {},

  middleLettersOnly: {},

  noMiddleName: {},

  hideMiddleName: {},

  middle: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.isAliasOrNickname) {
        return {
          presence: false,
          length: {
            is: 0,
            message: 'error.pvq.name.last.dataIntegrity.aliasOrNickname',
          }
        };
      }

      if (options?.requireMiddleName === false) { return {}; }
    }

    if (attributes?.hideMiddleName || attributes?.noMiddleName || attributes?.middleUnknown) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.middle.dataIntegrity.checkedWithValue',
        },
      };
    }

    if (attributes?.middleLettersOnly) {
      return {
        presence: true,
        length: {
          minimum: 1,
          maximum: 100,
          wrongLength: 'error.pvq.name.lettersonly.length'
        },
        format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
      };
    }

    return {
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooShort: 'error.pvq.name.middle.length.tooshort',
        tooLong: 'error.pvq.name.middle.length.toolong',
      },
      format: { pattern: namePatternPvq, message: 'error.pvq.name.middle.pattern' },
    };
  },

  suffix: {
    inclusion: { within: nameSuffixes, message: 'error.pvq.suffix.inclusion' },
  },

  suffixOther: (value, attributes) => {
    if (attributes?.suffix === 'Other') {
      return {
        presence: true,
        format: { pattern: suffixPattern, message: 'error.pvq.name.suffixOther.pattern' },
        length: {
          maximum: 100,
          tooLong: 'error.pvq.name.suffixOther.length.toolong',
        },
      };
    }

    return {};
  },
};

export default pvqNameModelValidator;

export function createDuplicateForeignContactsAliasNameValidator(
  value: DeepSafePartial<BranchCollectionModel<ForeignContactsItemAliasesModel>> | BranchCollectionModel<ForeignContactsItemAliasesModel>,
  attributes: DeepSafePartial<ForeignContactsItemModel>
) {
  const validator: ValidateJSModel<PVQNameModel> = { ...pvqNameModelValidator };
  const nameMatches = (compare1: PVQNameModel | DeepSafePartial<PVQNameModel> | undefined, compare2: PVQNameModel | DeepSafePartial<PVQNameModel> | undefined) => ( // eslint-disable-line max-len
    compare1 && compare2
    && compare1.first?.toLowerCase() === compare2.first?.toLowerCase()
    && compare1.middle?.toLowerCase() === compare2.middle?.toLowerCase()
    && compare1.last?.toLowerCase() === compare2.last?.toLowerCase()
    && compare1.suffix?.toLowerCase() === compare2.suffix?.toLowerCase()
    && compare1.suffixOther?.toLowerCase() === compare2.suffixOther?.toLowerCase()
    && (compare1.first || compare1.middle || compare1.last)
  );
  const duplicateAliasNames: (PVQNameModel | DeepSafePartial<PVQNameModel> | undefined)[] = [];
  // for each value.items as alias // if Item.Alias {first,middle,last, suffix } exists twice then validate an exclusion error
  if (value?.items) {
    for (const alias of value.items) {
      const sameItems = value.items.filter((x) => nameMatches(alias?.Item?.Alias, x?.Item?.Alias));
      if (sameItems?.length > 1) {
        duplicateAliasNames.push(alias?.Item?.Alias);
      }
    }
  }
  const duplicateError = 'error.pvq.name.duplicate.alias';

  validator.first = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (attributes?.noFirstName) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.first.dataIntegrity.checkedWithValue',
        },
      };
    }
    if (attributes?.firstLettersOnly) {
      return {
        presence: true,
        length: {
          minimum: 1,
          maximum: 100,
          wrongLength: 'error.pvq.name.lettersonly.length'
        },
        format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
      };
    }
    return {
      presence: true,
      format: { pattern: namePatternPvq, message: 'error.pvq.name.first.pattern' },
      length: (value, attributes) => ({ // eslint-disable-line no-shadow
        minimum: 2,
        maximum: 100,
        tooShort: 'error.pvq.name.first.length.tooshort',
        tooLong: 'error.pvq.name.first.length.toolong',
      }),
    };
  };

  validator.last = (value, attributes) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    return {
      format: { pattern: namePatternPvq, message: 'error.pvq.name.last.pattern' },
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooLong: 'error.pvq.name.last.length'
      },
    };
  };
  validator.middle = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (options?.hideMiddleName || attributes?.hideMiddleName || attributes?.noMiddleName || attributes?.middleUnknown) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.middle.dataIntegrity.checkedWithValue',
        },
      };
    }

    if (attributes?.middleLettersOnly) {
      return {
        presence: true,
        length: {
          minimum: 1,
          maximum: 100,
          wrongLength: 'error.pvq.name.lettersonly.length'
        },
        format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
      };
    }

    return {
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooShort: 'error.pvq.name.middle.length.tooshort',
        tooLong: 'error.pvq.name.middle.length.toolong',
      },
      format: { pattern: namePatternPvq, message: 'error.pvq.name.middle.pattern' },
    };
  };

  validator.suffix = (value, attributes) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    return {
      inclusion: { within: nameSuffixes, message: 'error.pvq.suffix.inclusion' },
    };
  };

  validator.suffixOther = (value, attributes) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (attributes?.suffix === 'Other') {
      return {
        presence: true,
        format: { pattern: suffixPattern, message: 'error.pvq.name.suffixOther.pattern' },
        length: {
          maximum: 100,
          tooLong: 'error.pvq.name.suffixOther.length.toolong',
        },
      };
    }

    return {};
  };

  return validator;
}
